import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import {
  GetOrganizationTalentIndicatorsQuery,
  GetOrganizationTalentIndicatorsQueryVariables,
  OrgSettingsOrganizationFragmentFragment,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import Table, {
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableContainer,
  TableHeadCell,
  TableHeadRow,
} from "@components/table/table";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { assertEdgesNonNull, contactUsCallback } from "@helpers/helpers";

import getOrganizationTalentIndicatorsQuery from "../graphql/get-organization-talent-indicators-query";
import EditTalentIndicatorModal, {
  TalentIndicator,
} from "./talent-indicators/edit-talent-indicator-modal";

const TalentIndicators = ({
  organization,
}: {
  organization: OrgSettingsOrganizationFragmentFragment;
}) => {
  const [editingTalentIndicator, setEditingTalentIndicator] =
    useState<TalentIndicator | null>(null);
  const { data, loading, refetch } = useQuery<
    GetOrganizationTalentIndicatorsQuery,
    GetOrganizationTalentIndicatorsQueryVariables
  >(getOrganizationTalentIndicatorsQuery, {
    variables: {
      organizationId: organization.id,
    },
  });

  const talentIndicators = useMemo(
    () =>
      data?.talentIndicators ? assertEdgesNonNull(data.talentIndicators) : [],
    [data],
  );

  const showEmployeeCustomNameColumn = useMemo(() => {
    return talentIndicators
      .filter(({ isManagerIndicator }) => !isManagerIndicator)
      .some(({ customName }) => customName && customName.trim() !== "");
  }, [talentIndicators]);
  const showEmployeeQuestionTitleColumn = useMemo(() => {
    return talentIndicators
      .filter(({ isManagerIndicator }) => !isManagerIndicator)
      .some(
        ({ questionTitle }) => questionTitle && questionTitle.trim() !== "",
      );
  }, [talentIndicators]);
  const showManagerCustomNameColumn = useMemo(() => {
    return talentIndicators
      .filter(({ isManagerIndicator }) => isManagerIndicator)
      .some(({ customName }) => customName && customName.trim() !== "");
  }, [talentIndicators]);
  const showManagerQuestionTitleColumn = useMemo(() => {
    return talentIndicators
      .filter(({ isManagerIndicator }) => isManagerIndicator)
      .some(
        ({ questionTitle }) => questionTitle && questionTitle.trim() !== "",
      );
  }, [talentIndicators]);

  if (loading) {
    return (
      <div className="mt-12 flex justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div>
      {editingTalentIndicator && (
        <EditTalentIndicatorModal
          talentIndicator={editingTalentIndicator}
          onClose={() => setEditingTalentIndicator(null)}
          onTalentIndicatorUpdated={() => refetch()}
        />
      )}

      <h2 className="text-2xl pb-6 font-semibold text-gray-800">
        Talent Indicators
      </h2>

      <div className="p-4 bg-gray-100 rounded-md flex gap-4 text-gray-800 text-sm">
        <TopicflowIcon type="info" className="h-5 w-5 text-gray-600" />
        <div>
          <div className="font-semibold mb-1">Want to add more indicators?</div>
          <div>
            If there are some indicators that you would like added, please
            contact Topicflow directly.
          </div>
          <Button
            className="mt-4"
            onClick={contactUsCallback}
            text="Contact Us"
          />
        </div>
      </div>

      <div className="mt-12">
        <h3 className="text-lg font-semibold text-gray-800">
          Employee Indicators
        </h3>
        <TableContainer scroll className="mt-4">
          <Table className="w-full">
            <TableHeadRow>
              <TableHeadCell>Indicator Name</TableHeadCell>
              <TableHeadCell>Description</TableHeadCell>
              {showEmployeeCustomNameColumn && (
                <TableHeadCell>Custom Name</TableHeadCell>
              )}
              {showEmployeeQuestionTitleColumn && (
                <TableHeadCell>Question Title</TableHeadCell>
              )}
              <TableHeadCell>Question Range</TableHeadCell>
              <TableHeadCell></TableHeadCell>
            </TableHeadRow>
            <TableBody>
              {talentIndicators
                .filter(({ isManagerIndicator }) => !isManagerIndicator)
                .map((talentIndicator) => {
                  return (
                    <TableBodyRow key={talentIndicator.id}>
                      <TableBodyCell className="w-48">
                        {talentIndicator.name}
                      </TableBodyCell>
                      <TableBodyCell className="w-96">
                        {!isEmptyValue(talentIndicator.description) && (
                          <TextareaWysiwyg
                            editable={false}
                            value={talentIndicator.description}
                          />
                        )}
                      </TableBodyCell>
                      {showEmployeeCustomNameColumn && (
                        <TableBodyCell>
                          {talentIndicator.customName}
                        </TableBodyCell>
                      )}
                      {showEmployeeQuestionTitleColumn && (
                        <TableBodyCell>
                          {talentIndicator.questionTitle}
                        </TableBodyCell>
                      )}
                      <TableBodyCell>
                        {`${talentIndicator.labels[0]} → ${
                          talentIndicator.labels[
                            talentIndicator.labels.length - 1
                          ]
                        } (${talentIndicator.scaleStartsAt} - ${
                          talentIndicator.scale
                        })`}
                      </TableBodyCell>
                      <TableBodyCell className="flex justify-end">
                        <Button
                          theme={buttonTheme.text}
                          onClick={() =>
                            setEditingTalentIndicator(talentIndicator)
                          }
                        >
                          <TopicflowIcon type="edit" className="h-4 w-4" />
                        </Button>
                      </TableBodyCell>
                    </TableBodyRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div className="mt-12">
        <h3 className="text-lg font-semibold text-gray-800">
          Manager Indicators
        </h3>
        <TableContainer scroll className="mt-4">
          <Table className="w-full">
            <TableHeadRow>
              <TableHeadCell>Indicator Name</TableHeadCell>
              <TableHeadCell>Description</TableHeadCell>
              {showManagerCustomNameColumn && (
                <TableHeadCell>Custom Name</TableHeadCell>
              )}
              {showManagerQuestionTitleColumn && (
                <TableHeadCell>Question Title</TableHeadCell>
              )}
              <TableHeadCell>Question Range</TableHeadCell>
              <TableHeadCell></TableHeadCell>
            </TableHeadRow>
            <TableBody>
              {talentIndicators
                .filter(({ isManagerIndicator }) => isManagerIndicator)
                .map((talentIndicator) => {
                  return (
                    <TableBodyRow key={talentIndicator.id}>
                      <TableBodyCell className="w-48">
                        {talentIndicator.name}
                      </TableBodyCell>
                      <TableBodyCell className="w-96">
                        {!isEmptyValue(talentIndicator.description) && (
                          <TextareaWysiwyg
                            editable={false}
                            value={talentIndicator.description}
                          />
                        )}
                      </TableBodyCell>
                      {showManagerCustomNameColumn && (
                        <TableBodyCell>
                          {talentIndicator.customName}
                        </TableBodyCell>
                      )}
                      {showManagerQuestionTitleColumn && (
                        <TableBodyCell>
                          {talentIndicator.questionTitle}
                        </TableBodyCell>
                      )}
                      <TableBodyCell>
                        {`${talentIndicator.labels[0]} → ${
                          talentIndicator.labels[
                            talentIndicator.labels.length - 1
                          ]
                        } (${talentIndicator.scaleStartsAt} - ${
                          talentIndicator.scale
                        })`}
                      </TableBodyCell>
                      <TableBodyCell className="flex justify-end">
                        <Button
                          theme={buttonTheme.text}
                          onClick={() =>
                            setEditingTalentIndicator(talentIndicator)
                          }
                        >
                          <TopicflowIcon type="edit" className="h-4 w-4" />
                        </Button>
                      </TableBodyCell>
                    </TableBodyRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default TalentIndicators;
